import { useMemo, useState } from 'react';

import { CategoryPills, type CategoryFilter } from '@/components/CategoryPills';
import { ChallengeCard } from '@/components/ChallengeCard';
import { FeaturedBanner } from '@/components/FeaturedBanner';
import { SearchBar } from '@/components/SearchBar';
import { useChallenges } from '@/lib/challenges';

type SortOption = 'newest' | 'oldest' | 'most-jems' | 'least-jems' | 'popular';

export function HomePage() {
  const [activeCategory, setActiveCategory] = useState<CategoryFilter>('All');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('popular');
  const challengesQuery = useChallenges();
  const challenges = challengesQuery.data?.items ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return challenges.filter((c) => {
      const catMatch = activeCategory === 'All' || c.category === activeCategory;
      const searchMatch =
        q === '' ||
        c.title.toLowerCase().includes(q) ||
        c.brief.toLowerCase().includes(q) ||
        c.category.toLowerCase().includes(q);
      return catMatch && searchMatch;
    });
  }, [activeCategory, challenges, search]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    switch (sortBy) {
      case 'newest':
        copy.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
        break;
      case 'oldest':
        copy.sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
        break;
      case 'most-jems':
        copy.sort((a, b) => b.jemCount - a.jemCount);
        break;
      case 'least-jems':
        copy.sort((a, b) => a.jemCount - b.jemCount);
        break;
      case 'popular':
        // Until submissions are moved to the real API, popularity uses aggregate
        // participation rather than mock submission activity.
        copy.sort((a, b) => {
          const participation = b.jemCount + b.submissionCount - (a.jemCount + a.submissionCount);
          return participation || Date.parse(b.createdAt) - Date.parse(a.createdAt);
        });
        break;
    }
    return copy;
  }, [filtered, sortBy]);

  const featured = useMemo(
    () => [...challenges].sort((a, b) => b.jemCount - a.jemCount)[0],
    [challenges],
  );

  if (challengesQuery.isLoading) {
    return <div className="py-12 text-center text-sm text-slate-500">Loading challenges…</div>;
  }

  if (challengesQuery.isError) {
    return (
      <div className="py-12 text-center text-sm text-red-600">
        Could not load challenges. Please try again.
      </div>
    );
  }

  return (
    <>
      <SearchBar value={search} onChange={setSearch} />

      {featured && (
        <FeaturedBanner
          title={`${featured.title} — ${featured.jemCount} jems`}
          href={`/challenge/${featured.id}`}
        />
      )}

      <div className="mb-4">
        <CategoryPills active={activeCategory} onChange={setActiveCategory} />
      </div>

      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold tracking-tight lg:text-xl">Active challenges</h1>
          <p className="text-sm text-slate-500">{sorted.length} live</p>
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortOption)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          aria-label="Sort challenges"
        >
          <option value="popular">Popular</option>
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="most-jems">Most Jems</option>
          <option value="least-jems">Least Jems</option>
        </select>
      </div>

      {sorted.length === 0 ? (
        <div className="py-12 text-center text-sm text-slate-500">
          No challenges match your search.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {sorted.map((challenge) => (
            <ChallengeCard key={challenge.id} challenge={challenge} leadingSubmission={null} />
          ))}
        </div>
      )}
    </>
  );
}
